import {
  IconDefinition,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '../IconButton';
import { cva, VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import { Trans } from 'react-i18next';

const bannerVariants = cva(
  'box-border inline-flex items-center gap-4 rounded-xs border px-3.75 py-3.75 [&:has(.close-icon)]:py-1.75',
  {
    variants: {
      emphasis: {
        strong: '',
        weak: '',
      },
      type: {
        default: '',
        info: '',
        warning: '',
        error: '',
        success: '',
      },
    },
    compoundVariants: [
      {
        emphasis: 'strong',
        type: 'default',
        class: 'border-border-state-neutral-dark bg-surface-state-neutral-light text-text-state-neutral-light',
      },
      {
        emphasis: 'strong',
        type: 'warning',
        class: 'border-border-state-warning-dark bg-surface-state-warning-light text-text-state-warning-dark',
      },
      {
        emphasis: 'strong',
        type: 'error',
        class: 'border-border-state-error-dark bg-surface-state-error-light text-text-state-error-dark',
      },
      {
        emphasis: 'strong',
        type: 'info',
        class: 'border-border-state-info-dark bg-surface-state-info-light text-text-state-info-dark',
      },
      {
        emphasis: 'strong',
        type: 'success',
        class: 'border-border-state-success-dark bg-surface-state-success-light text-text-state-success-dark',
      },
      {
        emphasis: 'weak',
        type: 'default',
        class: 'border-border-state-neutral-light text-text-state-neutral-light',
      },
      {
        emphasis: 'weak',
        type: 'warning',
        class: 'border-border-state-warning-light text-text-state-warning-light',
      },
      {
        emphasis: 'weak',
        type: 'error',
        class: 'border-border-state-error-light text-text-state-error-light',
      },
      {
        emphasis: 'weak',
        type: 'info',
        class: 'border-border-state-info-light text-text-state-info-light',
      },
      {
        emphasis: 'weak',
        type: 'success',
        class: 'border-border-state-success-light text-text-state-success-light',
      },
    ],
    defaultVariants: {
      emphasis: 'weak',
      type: 'default',
    },
  },
);
type BannerVariants = VariantProps<typeof bannerVariants>;

const iconType: Record<NonNullable<VariantProps<typeof bannerVariants>['type']>, IconDefinition> = {
  default: faCircleInfo,
  info: faCircleInfo,
  warning: faTriangleExclamation,
  error: faCircleExclamation,
  success: faCircleCheck,
};

export type BannerProps = BannerVariants & {
  withCloseButton?: boolean;
  message: string;
  className?: string;
  onClose?: () => void;
  size?: 'medium' | 'jumbo';
};

export const Banner = ({
  message,
  emphasis,
  type,
  className,
  onClose,
  size = 'medium',
  withCloseButton,
}: BannerProps) => {
  const isJumbo = size === 'jumbo';

  return (
    <div className={bannerVariants({ emphasis, type, className })}>
      <FontAwesomeIcon className={clsx(isJumbo ? 'size-6!' : 'size-5!')} icon={iconType[type ?? 'default']} />
      <span className={clsx('w-full', isJumbo ? 'text-s24' : 'text-s16')}>
        <Trans components={{ strong: <strong /> }}>{message}</Trans>
      </span>
      {withCloseButton && (
        <IconButton
          className="close-icon text-inherit! hover:bg-surface-hover-lighter/20 focus:bg-surface-hover-lighter/40!"
          Icon={() => <FontAwesomeIcon icon={faXmark} />}
          size={isJumbo ? 'large' : 'small'}
          onClick={onClose}
        />
      )}
    </div>
  );
};
